import { useCallback } from 'react';

import { useTranslation } from '@/i18n';
import { fractionalLeaveService } from '@/api/domains/fractionalLeave';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { StatusFilter, SortKey, SortDirection } from './useFractionalLeaveList';


export interface UseFractionalLeaveExportParams {
  searchText?: string;
  statusFilter: StatusFilter;
  sortKey?: SortKey;
  sortDirection: SortDirection;
}

export interface UseFractionalLeaveExportResult {
  isExporting: boolean;
  onExport: (format: UIExportFormat) => Promise<void>;
}

export function useFractionalLeaveExport(
  { searchText, statusFilter, sortKey, sortDirection }: UseFractionalLeaveExportParams,
): UseFractionalLeaveExportResult {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => fractionalLeaveService.export({
      page: 1,
      pageSize: 9999, // Export all filtered records (not paginated)
      searchText: searchText || undefined,
      statusFilter,
      sort: sortKey
        ? { key: sortKey, direction: sortDirection.toLowerCase() as 'asc' | 'desc' }
        : undefined,
    }),
    successMessage: translate('fractionalLeave:messages.exportSuccess'),
    errorMessage: translate('fractionalLeave:messages.exportError'),
  });

  const onExport = useCallback(async (format: UIExportFormat) => {
    if (format !== 'EXCEL') {
      showNotification('error', translate('fractionalLeave:messages.exportError'));
      return;
    }
    await exportFile();
  }, [exportFile, showNotification, translate]);

  return { isExporting, onExport };
}
